import * as moment from 'moment-tz-60';
import { appInjector } from '../app-injector';
import { AppSettings } from '../settings/app-settings.model';

export const serverTimeZone = () => {
    const injector = appInjector();
    const appSettings = <AppSettings> injector.get(AppSettings);
    return appSettings.timeZone;
};

const EARLY_DATE = '1799-12-31';
export const CALENDAR_MIN_DATE = new Date(1800, 0, 1);
export const SERVER_MIN_DATE = new Date(0, 0, 1);

const getServerDateFromDateOrString = (initialDate: Date | string): string => convertToServerTz(initialDate).format('L');

const getServerTimeFromDateOrString = (initialDate: Date | string): string => convertToServerTz(initialDate).format('LT');

const getServerDateFromObject = (data: Object, dateLabel: string): string => convertToServerTz(data[dateLabel]).format('L');

const getServerTimeFromObject = (data: Object, dateLabel: string): string => convertToServerTz(data[dateLabel]).format('LT');

const convertToServerTz = (dateTime: string | Date, timeZone: string = serverTimeZone()): moment.Moment => {

    if (typeof dateTime === 'string') {
        dateTime = new Date(dateTime);
    }

    if (dateTime instanceof Date) {
        dateTime = dateTime.toISOString();
    }

    return moment.tz(dateTime, 'YYYY-MM-DD HH:mm:ss', timeZone);
};

const convertMomentToServerTzMoment = (moment: moment.Moment): moment.Moment => moment.tz(serverTimeZone());

const getServerMomentDate = () => convertMomentToServerTzMoment(moment()).startOf('day');

const makeServerMomentMidnight = (dateTime: string) => {
    const momentString = moment(dateTime).format('YYYY-MM-DD');
    return convertToServerTz(momentString);
};

const setServerDateFromInputEvent = (event: Event): Date => setServerDateFromString((event.currentTarget as HTMLInputElement).value);

const setServerDateFromString = (dateString: string): Date => {
    const dateMoment = moment(dateString, 'MM-DD-YYYY');

    if (dateMoment.isValid() && dateMoment.isAfter(EARLY_DATE)) {
        const dateMomentString = dateMoment.toISOString();
        return makeServerMomentMidnight(dateMomentString).toDate();
    }

    return null;
};

const validDate = (testDate: Date | string): boolean => {
    const testMoment = moment(testDate);
    return testDate && testMoment.isValid() && testMoment.isAfter(EARLY_DATE) && (typeof testDate !== 'string' || !!testDate.match(/\d{4}-\d{2}-\d{2}/));
};

export const getCurrentDate = (addition?: Object): Date => {
    const localDateString = moment().format('YYYY-MM-DD');
    const currentMoment = convertToServerTz(localDateString);
    const serverMoment = addition ? currentMoment.clone().add(addition) : currentMoment;
    return serverMoment.toDate();
};

export const getCurrentDateToString = (): string => moment().format('L');

export const getServerDate = (initialDate: Date | Object | string, dateLabel: string = undefined): string => {
    const regex = /\d{4}-\d{2}-\d{2}/;
    if ((initialDate instanceof Date && validDate(initialDate)) ||
        (typeof initialDate === 'string' && initialDate.match(regex))) {
        return getServerDateFromDateOrString(initialDate);
    } else if (initialDate instanceof Object && validDate(initialDate[dateLabel])) {
        return getServerDateFromObject(initialDate, dateLabel);
    } else if (initialDate instanceof Object && dateLabel) {
        initialDate[dateLabel] = null;
    }

    return null;
};

export const getServerDateTime = (initialDate: Date | Object | string, dateLabel: string = undefined): string => {
    const regex = /\d{4}-\d{2}-\d{2}/;
    if ((initialDate instanceof Date && validDate(initialDate)) ||
        (typeof initialDate === 'string' && initialDate.match(regex))) {
        return getServerDateFromDateOrString(initialDate) + ' ' + getServerTimeFromDateOrString(initialDate);
    } else if (initialDate instanceof Object && validDate(initialDate[dateLabel])) {
        return getServerDateFromObject(initialDate, dateLabel) + ' ' + getServerTimeFromObject(initialDate, dateLabel);
    } else if (initialDate instanceof Object && dateLabel) {
        initialDate[dateLabel] = null;
    }

    return null;
};

export const isStartAfterEnd = (start: string, end: string): boolean => !!start && !!end && moment(setServerDateFromString(start)).isAfter(setServerDateFromString(end));

export const isOutOfRange = (date: string, start: string, end: string): boolean => !!date && !!start && !!end && !moment(setServerDateFromString(date)).isBetween(setServerDateFromString(start), setServerDateFromString(end));

export const isDateAfterServerDate = (date: string): boolean => {
    let currentMoment = moment(date);
    let serverMoment = getServerMomentDate();

    if (!validDate(date)) {
        return false;
    }

    let result = currentMoment.isAfter(serverMoment.endOf('day'));

    return result;
};

export const formatDate = (date: string, format: string): string => validDate(date) && moment(date).format(format) || date;

// changes local time to match server midnight for the same date as the client.
// this should be used when saving a date that needs to be midnight in local server time.
export const setServerDate = (initialDateTime: Date | Event | string): Date => {
    if (initialDateTime instanceof Date && validDate(initialDateTime)) {
        return makeServerMomentMidnight(initialDateTime.toISOString()).toDate();
    } else if (initialDateTime instanceof Event) {
        return setServerDateFromInputEvent(initialDateTime);
    } else if (typeof initialDateTime === 'string') {
        return setServerDateFromString(initialDateTime);
    }

    return null;
};

export const setServerDateToString = (initialDateTime: Date | Event | string): string => {
    const serverDate = setServerDate(initialDateTime);
    return serverDate ? convertToServerTz(serverDate).format('L') : null;
};
